import type { MattingInfo } from '@meitu/x-design-ai-api';
import type {
    APP_NAME,
    CustomStyle,
    ENV,
    ImageMattingSdkType,
    ISDKInitOptions,
    MattingOptions,
    ThemeType,
} from './types';

function getIframeURL({
    locale,
    isIntl,
    env,
    appName,
}: {
    locale?: string;
    appName?: APP_NAME;
    isIntl: boolean;
    env: ENV;
}) {
    const localePath = locale ? `/${locale}` : '';
    const isDesignkitIntl = appName === 'designkit' && isIntl;
    const editorPath = 'cutout/sdk-editor';
    const XDesignHost = 'zawa.ai';
    const intlHost = window.location.hostname.match('x-design.starii.com') ? 'x-design.starii.com' : XDesignHost;
    const host = isDesignkitIntl ? 'designkit.com' : isIntl ? intlHost : 'designkit.cn';
    const envPrefix = env === 'release' ? (host.includes('zawa') ? '' : 'www.') : `${env}.`;

    return `https://${envPrefix}${host}${localePath}/${editorPath}`;
}

export class ImageMattingSdk implements ImageMattingSdkType {
    private isInited = false;

    private cacheFnList = [] as Array<() => void>;

    private onError = (e: Event | string) => {};

    private onCancel = () => {};

    private onReupload = () => {};

    private onMattingCompleted = (data: {
        imageURL: string;
        mattingInfo: MattingInfo;
        trackData?: Record<string, any>;
    }) => {};

    private onDownload = (data: { imageURL: string; mattingInfo: MattingInfo; trackData?: Record<string, any> }) => {};

    /* 可下载状态监听 */
    private onDownloadableUpdate = (data: boolean) => {};

    /* matting状态监听 */
    private onMattingStatusUpdate = (data: string) => {};

    /* task id 监听 */
    private onTaskIdUpdate = (data: string[]) => {};

    private tryMattingResolve = () => {};

    private tryMattingReject = () => {};

    private iframeCallbackFn;

    private initResolve = () => {};

    private iframe: HTMLIFrameElement;

    constructor() {
        this.iframe = document.createElement('iframe');
        const iframeCallbackFn = (event: MessageEvent) => {
            // ATTENTION: 抠图 iframe 往 抠图 SDK 发送消息
            if (
                /(designkit.cn)|(designkit.com)|(cutout\.meitu\.com)|(cutout\.designkit\.com)|localhost|(x-design\.starii\.com)|(x-design\.com)|(mcp\.meitu\.com)|(zawa\.ai)/.test(
                    event.origin,
                )
            ) {
                const { data = {} } = event;
                if (data.type === 'initialized') {
                    this.initedCacheFn();
                    this.initResolve();
                    this.isInited = true;
                }
                if (data.type === 'extract') {
                    this.onMattingCompleted({
                        imageURL: data.value,
                        mattingInfo: data.mattingInfo,
                        trackData: data.trackData,
                    });
                }
                if (data.type === 'download') {
                    this.onDownload({
                        imageURL: data.value,
                        mattingInfo: data.mattingInfo,
                        trackData: data.trackData,
                    });
                }
                if (data.type === 'mattinged') {
                    if (data.value) {
                        this.tryMattingResolve();
                    } else {
                        this.tryMattingReject();
                    }
                }
                if (data.type === 'cancel') {
                    this.onCancel();
                }
                if (data.type === 'error') {
                    this.onError(data.value);
                }
                if (data.type === 'reupload') {
                    this.onReupload();
                }
                if (data.type === 'downloadable_update') {
                    this.onDownloadableUpdate(data.value);
                }
                if (data.type === 'matting_status_update') {
                    this.onMattingStatusUpdate(data.value);
                }
                if (data.type === 'task_id_update') {
                    this.onTaskIdUpdate(data.value);
                }
            }
        };
        this.iframeCallbackFn = iframeCallbackFn;
    }

    private initedCacheFn() {
        if (this.cacheFnList.length) {
            this.cacheFnList.forEach((fn) => {
                fn();
            });
            this.cacheFnList = [];
        }
    }

    init(params: ISDKInitOptions): Promise<void> {
        const {
            parentDom,
            env = 'release',
            appName,
            theme,
            isIntl = false,
            appKey = '',
            iframeType,
            locale,
            xMtccClient,
            mtstatCommonParams,
            SDKParams,
            customStyle,
            onError,
            onCancel,
            onReupload,
            onMattingCompleted,
            onDownload,
            onDownloadableUpdate,
            onMattingStatusUpdate,
            onTaskIdUpdate,
        } = params;

        if (onError) {
            this.onError = onError;
        }
        if (onCancel) {
            this.onCancel = onCancel;
        }
        if (onReupload) {
            this.onReupload = onReupload;
        }

        if (onDownload) {
            this.onDownload = onDownload;
        }

        if (onMattingCompleted) {
            this.onMattingCompleted = onMattingCompleted;
        }

        if (onDownloadableUpdate) {
            this.onDownloadableUpdate = onDownloadableUpdate;
        }

        if (onMattingStatusUpdate) {
            this.onMattingStatusUpdate = onMattingStatusUpdate;
        }

        if (onTaskIdUpdate) {
            this.onTaskIdUpdate = onTaskIdUpdate;
        }

        return new Promise((resolve, reject) => {
            this.initResolve = resolve;
            if (!this.isInited) {
                const url = getIframeURL({ locale, env, isIntl, appName });
                const queryParams: Record<string, string> = {
                    iframeType: iframeType || '',
                    xMtccClient: xMtccClient || '',
                    theme: theme || '',
                    customStyle: customStyle ? JSON.stringify(customStyle) : '',
                    origin: SDKParams?.origin || '',
                    platform: SDKParams?.platform || '',
                    is_trial: SDKParams?.is_trial ? '1' : '',
                    client_id: SDKParams?.client_id || '',
                    is_qimi: SDKParams?.is_qimi ? '1' : '',
                    pc_version: SDKParams?.pc_version || '',
                    appKey,
                };

                if (/__mtstat_trace__/.test(window.location.href)) {
                    queryParams.__mtstat_trace__ = /__mtstat_trace__=1/.test(window.location.href) ? '1' : '0';
                }

                if (mtstatCommonParams) {
                    queryParams.mtstatCommonParams = encodeURIComponent(JSON.stringify(mtstatCommonParams));
                }

                const queryString = [...Object.entries(queryParams)].map(([key, value]) => `${key}=${value}`).join('&');

                this.iframe.src = `${url}?${queryString}`;
                this.iframe.onerror = (e) => {
                    this.onError && this.onError(e);
                    reject();
                };
                parentDom.appendChild(this.iframe);
                window.addEventListener('message', this.iframeCallbackFn, false);
            }
        });
    }

    /* 执行抠图 */
    async tryMatting({ imageInfo, extraInfo }: MattingOptions): Promise<void> {
        const postMessage = () => {
            this.iframe.contentWindow?.postMessage(
                {
                    type: 'tryMatting',
                    imageInfo: JSON.stringify(imageInfo),
                    extraInfo: extraInfo ? JSON.stringify(extraInfo) : '',
                },
                '*',
            );
        };
        if (this.isInited) {
            postMessage();
        } else {
            this.cacheFnList.push(() => {
                postMessage();
            });
        }
        return new Promise((resolve, reject) => {
            this.tryMattingResolve();
            this.tryMattingReject();
            this.tryMattingResolve = resolve;
            this.tryMattingReject = reject;
        });
    }

    /* 执行保存 */
    extract() {
        this.iframe.contentWindow?.postMessage(
            {
                type: 'startExtract',
            },
            '*',
        );
    }

    adjustPagePositionAndScale() {
        this.iframe.contentWindow?.postMessage(
            {
                type: 'adjustPagePositionAndScale',
            },
            '*',
        );
    }

    /* 设置成本分摊参数 */
    setMTCCClient(data: string) {
        this.iframe.contentWindow?.postMessage(
            {
                type: 'setMTCCClient',
                data,
            },
            '*',
        );
    }

    /* 设置主题 */
    setTheme(theme: ThemeType) {
        this.iframe.contentWindow?.postMessage(
            {
                type: 'setTheme',
                data: theme,
            },
            '*',
        );
    }

    // customStyle
    setCustomStyle(customStyle: CustomStyle) {
        this.iframe.contentWindow?.postMessage(
            {
                type: 'setCustomStyle',
                data: JSON.stringify(customStyle),
            },
            '*',
        );
    }

    destroy() {
        const destroyFn = () => {
            this.isInited = false;
            window.removeEventListener('message', this.iframeCallbackFn, false);
        };
        if (this.isInited) {
            destroyFn();
            return;
        }
        this.cacheFnList.push(async () => {
            destroyFn();
        });
    }
}
